import React, { useState } from 'react';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const BillingPrintModal = ({ isOpen, orderId, tableId, paymentMethod }) => {
  const navigate = useNavigate();
  const [printing, setPrinting] = useState(false);

  const handleIgnore = () => {
    navigate('/dashboard');
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      // Mirrors pos_web's post-payment "Print Billing Docket?" modal (order/print?docket=billing),
      // not the pre-payment "Print Bill" button (cart/printdocket) — this endpoint resolves a
      // sub-order up to its parent and auto-detects dine-in multi-order tables, so every item
      // across a table's combined orders ends up on the one billing docket.
      await callAPI(apiUrls.ORDER_PRINT_URL, { order_id: orderId, docket: 'billing' }, 'GET');
      toast.success('Bill printed');
    } catch {
      toast.error('Print failed');
    } finally {
      setPrinting(false);
      navigate('/dashboard');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.75)',
              zIndex: 9998,
            }}
          />

          {/* Centered card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1, transition: { duration: 0.25, ease: 'easeOut' } }}
            exit={{ opacity: 0, scale: 0.85, transition: { duration: 0.2 } }}
            style={{
              position: 'fixed', inset: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{
              pointerEvents: 'auto',
              background: '#1e1e2e',
              borderRadius: '16px',
              padding: '40px 36px 32px',
              width: '100%',
              maxWidth: '400px',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}>
              {/* Icon */}
              <div style={{
                width: 64, height: 64,
                borderRadius: '50%',
                background: 'rgba(99,102,241,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
              </div>

              <h4 style={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 8, fontSize: '1.2rem' }}>
                Print Billing Docket?
              </h4>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: 32, lineHeight: 1.5 }}>
                Would you like to print the billing docket on the primary printer?
              </p>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={handleIgnore}
                  disabled={printing}
                  style={{
                    flex: 1, padding: '13px 0',
                    borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.06)', color: '#cbd5e1',
                    fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                >
                  Ignore
                </button>
                <button
                  onClick={handlePrint}
                  disabled={printing}
                  style={{
                    flex: 1, padding: '13px 0',
                    borderRadius: '10px', border: 'none',
                    background: printing ? '#4f46e5aa' : '#4f46e5',
                    color: '#fff', fontSize: '0.95rem', fontWeight: 600,
                    cursor: printing ? 'not-allowed' : 'pointer',
                    transition: 'background 0.15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                  onMouseOver={e => { if (!printing) e.currentTarget.style.background = '#4338ca'; }}
                  onMouseOut={e => { if (!printing) e.currentTarget.style.background = '#4f46e5'; }}
                >
                  {printing ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      Printing...
                    </>
                  ) : 'Print'}
                </button>
              </div>
            </div>
          </motion.div>

          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </>
      )}
    </AnimatePresence>
  );
};

export default BillingPrintModal;
